import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PersonalDataSection from './PersonalDataSection';
import WorkExperienceSection from './WorkExperienceSection';
import FinancialDataSection from './FinancialDataSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import ProfessionalCategorySection from './ProfessionalCategorySection';
import RegionalBonusesSection from './RegionalBonusesSection';

/**
 * Компонент основной формы расчета пенсии
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  projection,
  setProjection,
  professionalCategories,
  russianRegions,
  investmentStrategies,
  saveScenario 
}) => {
  
  // Состояние для имени сохраняемого сценария
  const [saveScenarioName, setSaveScenarioName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем доходность инвестиций при выборе инвестиционной стратегии
      if (name === 'selfInvestmentType') {
        const selectedStrategy = investmentStrategies.find(strategy => strategy.id === fieldValue);
        if (selectedStrategy) {
          newState.investmentReturnRate = selectedStrategy.returnRate.toString();
        }
      }

      // Если изменился пол, обновляем пенсионный возраст
      if (name === 'gender') {
        const retirementAge = value === 'male' ? 65 : 60;
        newState.retirementAge = retirementAge.toString();
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeMaternityCapital' && !checked) {
        // Логика сброса связанных полей для материнского капитала, если нужна
      }
      
      if (name === 'includeRegionalSupport' && !checked) {
        newState.region = 'moscow';
      }
      
      if (name === 'includeProfessionalCoefficient' && !checked) {
        newState.professionalCategory = 'default';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [investmentStrategies, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      birthDate, gender, retirementAge, salaryAmount, workExperienceYears,
      additionalExperienceYears, futureWorkYears, savingsAmount, monthlyContribution,
      annualIncreaseRate, investmentReturnRate, inflationRate, pensionType,
      voluntaryPensionType, npfReturnRate, selfInvestmentType, includeMaternityCapital,
      includeRegionalSupport, region, includeRuralBonus, includeProfessionalCoefficient,
      professionalCategory
    } = formData;

    // Проверка даты рождения
    if (!birthDate) {
      newErrors.birthDate = 'Пожалуйста, укажите дату рождения.';
    } else {
      const birthDateObj = new Date(birthDate);
      const currentYear = new Date().getFullYear();
      
      if (isNaN(birthDateObj.getTime())) {
        newErrors.birthDate = 'Некорректная дата рождения.';
      } else if (birthDateObj.getFullYear() < currentYear - 100) {
        newErrors.birthDate = 'Дата рождения слишком ранняя.';
      } else if (birthDateObj.getFullYear() > currentYear - 18) {
        newErrors.birthDate = 'Для расчёта пенсии возраст должен быть от 18 лет.';
      }
    }

    // Проверка пола
    if (!gender || !['male', 'female'].includes(gender)) {
      newErrors.gender = 'Выберите пол.';
    }

    // Проверка пенсионного возраста
    const numRetirementAge = parseIntOrDefault(retirementAge, -1);
    if (numRetirementAge < 45 || numRetirementAge > 100) {
      newErrors.retirementAge = 'Пенсионный возраст должен быть от 45 до 100 лет.';
    }

    // Проверка зарплаты
    const numSalaryAmount = parseFloatOrDefault(salaryAmount, -1);
    if (numSalaryAmount <= 0) {
      newErrors.salaryAmount = 'Зарплата должна быть положительной.';
    } else if (numSalaryAmount > 1000000) {
      newErrors.salaryAmount = 'Слишком высокая зарплата для корректного расчёта.';
    }

    // Проверка стажа
    const numWorkExperienceYears = parseFloatOrDefault(workExperienceYears, -1);
    const numAdditionalExperienceYears = parseFloatOrDefault(additionalExperienceYears, 0);
    const numFutureWorkYears = parseFloatOrDefault(futureWorkYears, -1);
    
    if (numWorkExperienceYears < 0) {
      newErrors.workExperienceYears = 'Стаж не может быть отрицательным.';
    } else if (numWorkExperienceYears > 60) {
      newErrors.workExperienceYears = 'Стаж не может превышать 60 лет.';
    }
    
    if (numAdditionalExperienceYears < 0) {
      newErrors.additionalExperienceYears = 'Дополнительный стаж не может быть отрицательным.';
    } else if (numAdditionalExperienceYears > 30) {
      newErrors.additionalExperienceYears = 'Дополнительный стаж не может превышать 30 лет.';
    }
    
    if (numFutureWorkYears < 0) {
      newErrors.futureWorkYears = 'Планируемый стаж не может быть отрицательным.';
    } else if (numFutureWorkYears > 50) {
      newErrors.futureWorkYears = 'Планируемый стаж не может превышать 50 лет.';
    }

    // Проверка суммы накоплений
    const numSavingsAmount = parseFloatOrDefault(savingsAmount, 0);
    if (numSavingsAmount < 0) {
      newErrors.savingsAmount = 'Накопления не могут быть отрицательными.';
    } else if (numSavingsAmount > 100000000) {
      newErrors.savingsAmount = 'Слишком большая сумма накоплений для корректного расчёта.';
    }

    // Проверка ежемесячных отчислений
    const numMonthlyContribution = parseFloatOrDefault(monthlyContribution, 0);
    if (numMonthlyContribution < 0) {
      newErrors.monthlyContribution = 'Ежемесячные отчисления не могут быть отрицательными.';
    } else if (numMonthlyContribution > 100000) {
      newErrors.monthlyContribution = 'Слишком большая сумма ежемесячных отчислений.';
    }

    // Проверка процентных ставок
    const numAnnualIncreaseRate = parseFloatOrDefault(annualIncreaseRate, -1);
    if (numAnnualIncreaseRate < 0 || numAnnualIncreaseRate > 20) {
      newErrors.annualIncreaseRate = 'Рост зарплаты должен быть от 0% до 20%.';
    }
    
    const numInvestmentReturnRate = parseFloatOrDefault(investmentReturnRate, -1);
    if (numInvestmentReturnRate < 0 || numInvestmentReturnRate > 25) {
      newErrors.investmentReturnRate = 'Доходность инвестиций должна быть от 0% до 25%.';
    }
    
    const numInflationRate = parseFloatOrDefault(inflationRate, -1);
    if (numInflationRate < 0 || numInflationRate > 15) {
      newErrors.inflationRate = 'Инфляция должна быть от 0% до 15%.';
    }
    
    const numNpfReturnRate = parseFloatOrDefault(npfReturnRate, -1);
    if (pensionType === 'voluntary' && voluntaryPensionType === 'npf' && (numNpfReturnRate < 0 || numNpfReturnRate > 15)) {
      newErrors.npfReturnRate = 'Доходность НПФ должна быть от 0% до 15%.';
    }

    // Проверка типа пенсии
    if (!pensionType || !['mandatory', 'voluntary', 'both'].includes(pensionType)) {
      newErrors.pensionType = 'Выберите тип пенсии.';
    }

    // Проверка типа накопительной пенсии
    if ((pensionType === 'voluntary' || pensionType === 'both') && (!voluntaryPensionType || !['npf', 'life_insurance', 'individual'].includes(voluntaryPensionType))) {
      newErrors.voluntaryPensionType = 'Выберите тип накопительной пенсии.';
    }

    // Проверка типа инвестиций
    if ((pensionType === 'voluntary' || pensionType === 'both') && voluntaryPensionType === 'individual' && (!selfInvestmentType || !['conservative', 'balanced', 'aggressive'].includes(selfInvestmentType))) {
      newErrors.selfInvestmentType = 'Выберите тип инвестиционной стратегии.';
    }

    // Проверка региона для региональных доплат
    if (includeRegionalSupport && (!region || !russianRegions.some(r => r.id === region))) {
      newErrors.region = 'Выберите регион для региональных доплат.';
    }

    // Проверка профессиональной категории
    if (includeProfessionalCoefficient && (!professionalCategory || !professionalCategories.some(c => c.id === professionalCategory))) {
      newErrors.professionalCategory = 'Выберите профессиональную категорию.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, russianRegions, professionalCategories, setErrors]);

  // Функция для расчета пенсии
  const calculatePension = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setProjection([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const birthDateObj = new Date(formData.birthDate);
        const currentYear = new Date().getFullYear();
        const currentAge = currentYear - birthDateObj.getFullYear();
        
        const retirementAge = parseIntOrDefault(formData.retirementAge, formData.gender === 'male' ? 65 : 60);
        const yearsToRetirement = Math.max(0, retirementAge - currentAge);
        
        const monthlySalary = parseFloatOrDefault(formData.salaryAmount, 0);
        const annualSalary = monthlySalary * 12;
        
        const workExperience = parseFloatOrDefault(formData.workExperienceYears, 0);
        const additionalExperience = parseFloatOrDefault(formData.additionalExperienceYears, 0);
        const futureWorkYears = parseFloatOrDefault(formData.futureWorkYears, 0);
        const totalExperience = workExperience + additionalExperience + Math.min(futureWorkYears, yearsToRetirement);
        
        const currentSavings = parseFloatOrDefault(formData.savingsAmount, 0);
        const monthlyContribution = parseFloatOrDefault(formData.monthlyContribution, 0);
        
        const annualIncreaseRate = parseFloatOrDefault(formData.annualIncreaseRate, 4) / 100;
        const investmentReturnRate = parseFloatOrDefault(formData.investmentReturnRate, 7) / 100;
        const inflationRate = parseFloatOrDefault(formData.inflationRate, 4) / 100;
        const npfReturnRate = parseFloatOrDefault(formData.npfReturnRate, 6) / 100;
        
        // --- Расчет страховой пенсии ---
        // Основные параметры системы (актуальны на 2025 год)
        const pensionCoefficient = 116.2; // Стоимость пенсионного коэффициента
        const fixedPayment = 7567.33; // Фиксированная выплата к страховой пенсии
        const maxContributionBase = 1917000; // Предельная величина базы для начисления страховых взносов
        const contributionRate = 0.22; // Ставка страховых взносов (22%)
        const pensionPointsMax = 10; // Максимальное количество пенсионных коэффициентов за год
        
        // Расчет индивидуального пенсионного коэффициента (ИПК)
        const calculateAnnualIpk = (salary) => {
          const contributionAmount = Math.min(salary, maxContributionBase) * contributionRate;
          const maxContributionAmount = maxContributionBase * contributionRate;
          return (contributionAmount / maxContributionAmount) * pensionPointsMax;
        };
        
        // Базовый расчет страховой пенсии
        let totalIpk = 0;
        let currentIpkAccumulated = calculateAnnualIpk(annualSalary) * workExperience;
        
        // Применение профессиональных коэффициентов если выбраны
        let professionalCoefficientBonus = 1;
        let earlyRetirementYears = 0;
        
        if (formData.includeProfessionalCoefficient) {
          const professionalCategory = professionalCategories.find(c => c.id === formData.professionalCategory);
          if (professionalCategory) {
            professionalCoefficientBonus = professionalCategory.coefficientBonus;
            earlyRetirementYears = professionalCategory.retirementBonus;
          }
        }
        
        // Применяем профессиональный коэффициент к накопленным баллам
        currentIpkAccumulated *= professionalCoefficientBonus;
        
        // Проекция будущих пенсионных баллов
        const projectionYears = Math.max(retirementAge - currentAge + 30, 10); // Проекция до конца жизни (примерно)
        const projectionData = [];
        
        let cumulativeSavings = currentSavings;
        let currentSalary = monthlySalary;
        let yearIpk = calculateAnnualIpk(currentSalary * 12);
        totalIpk = currentIpkAccumulated;
        
        for (let year = 0; year < projectionYears; year++) {
          const age = currentAge + year;
          const isRetirementYear = age >= retirementAge - earlyRetirementYears;
          
          // Увеличиваем зарплату ежегодно, если еще не на пенсии
          if (!isRetirementYear) {
            currentSalary *= (1 + annualIncreaseRate);
            yearIpk = calculateAnnualIpk(currentSalary * 12) * professionalCoefficientBonus;
            totalIpk += yearIpk;
          } else {
            yearIpk = 0; // На пенсии больше не накапливаем ИПК
          }
          
          // Расчет накоплений
          let yearlyContribution = monthlyContribution * 12;
          if (isRetirementYear) {
            yearlyContribution = 0; // Прекращаем взносы на пенсии
          }
          
          // Инвестиционный доход
          let investmentReturn = 0;
          if (formData.pensionType === 'voluntary' || formData.pensionType === 'both') {
            let effectiveRate = investmentReturnRate;
            if (formData.voluntaryPensionType === 'npf') {
              effectiveRate = npfReturnRate;
            }
            
            investmentReturn = cumulativeSavings * effectiveRate;
          }
          
          cumulativeSavings += yearlyContribution + investmentReturn;
          
          // Расчет пенсионных выплат
          let insurancePensionPayment = 0;
          let voluntaryPensionPayment = 0;
          
          if (isRetirementYear) {
            // Страховая пенсия
            if (formData.pensionType === 'mandatory' || formData.pensionType === 'both') {
              insurancePensionPayment = (totalIpk * pensionCoefficient + fixedPayment) / 12;
            }
            
            // Расчет накопительной части
            if ((formData.pensionType === 'voluntary' || formData.pensionType === 'both') && isRetirementYear) {
              // Рассчитываем аннуитетный платеж на основе накоплений
              // Примерно: накопления / ожидаемый период выплаты (например, 20 лет * 12 месяцев)
              const expectedPaymentPeriod = 240; // 20 лет
              voluntaryPensionPayment = cumulativeSavings / expectedPaymentPeriod;
              
              // Уменьшаем накопления на сумму выплаты
              cumulativeSavings -= voluntaryPensionPayment * 12;
            }
          }
          
          // Учитываем региональные доплаты
          let regionalBonus = 0;
          if (formData.includeRegionalSupport && isRetirementYear) {
            const selectedRegion = russianRegions.find(r => r.id === formData.region);
            if (selectedRegion) {
              const minPension = selectedRegion.minPension;
              const totalPension = insurancePensionPayment + voluntaryPensionPayment;
              
              if (totalPension < minPension / 12) {
                regionalBonus = (minPension / 12) - totalPension;
              }
            }
          }
          
          // Учитываем сельские надбавки
          let ruralBonus = 0;
          if (formData.includeRuralBonus && isRetirementYear) {
            // Для сельских жителей с стажем от 30 лет надбавка 25% от фиксированной выплаты
            if (totalExperience >= 30) {
              ruralBonus = (fixedPayment * 0.25) / 12;
            }
          }
          
          // Корректируем пенсию на инфляцию для более реалистичного отображения
          const inflationFactor = Math.pow(1 + inflationRate, year);
          const realInsurancePension = insurancePensionPayment / inflationFactor;
          const realVoluntaryPension = voluntaryPensionPayment / inflationFactor;
          const realRegionalBonus = regionalBonus / inflationFactor;
          const realRuralBonus = ruralBonus / inflationFactor;
          const realTotalPension = realInsurancePension + realVoluntaryPension + realRegionalBonus + realRuralBonus;
          
          // Добавляем данные в проекцию
          projectionData.push({
            year: currentYear + year,
            age,
            monthlyIncome: isRetirementYear ? 0 : currentSalary,
            monthlySavingsContribution: yearlyContribution / 12,
            cumulativeSavings,
            insurancePension: realInsurancePension,
            voluntaryPension: realVoluntaryPension,
            regionalBonus: realRegionalBonus,
            ruralBonus: realRuralBonus,
            totalPension: realTotalPension,
            ipkAccumulated: totalIpk,
            yearIpk: yearIpk,
            inflationFactor
          });
        }
        
        // Получаем данные о пенсии на момент выхода на пенсию
        const retirementIndex = projectionData.findIndex(p => p.age >= retirementAge - earlyRetirementYears);
        const retirementData = retirementIndex >= 0 ? projectionData[retirementIndex] : null;
        
        // Расчет реплейсмент рейта (коэффициент замещения) - отношение пенсии к зарплате перед выходом на пенсию
        let replacementRate = 0;
        if (retirementIndex > 0 && retirementData) {
          const prePensionSalary = projectionData[retirementIndex - 1].monthlyIncome;
          replacementRate = prePensionSalary > 0 ? (retirementData.totalPension / prePensionSalary) * 100 : 0;
        }
        
        // Формируем результаты
        const results = {
          // Основные результаты
          currentAge,
          retirementAge: retirementAge - earlyRetirementYears,
          yearsToRetirement: Math.max(0, retirementAge - earlyRetirementYears - currentAge),
          
          // Данные о пенсии
          monthlyPension: retirementData ? retirementData.totalPension : 0,
          insurancePension: retirementData ? retirementData.insurancePension : 0,
          voluntaryPension: retirementData ? retirementData.voluntaryPension : 0,
          regionalBonus: retirementData ? retirementData.regionalBonus : 0,
          ruralBonus: retirementData ? retirementData.ruralBonus : 0,
          
          // Накопления
          totalSavingsAtRetirement: retirementData ? retirementData.cumulativeSavings : 0,
          
          // Пенсионные баллы
          totalPensionPoints: retirementData ? retirementData.ipkAccumulated : 0,
          
          // Коэффициент замещения
          replacementRate,
          
          // Другие показатели
          totalExperience,
          professionalCoefficientBonus,
          earlyRetirementYears
        };
        
        setResults(results);
        setProjection(projectionData);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setProjection([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, professionalCategories, russianRegions, setResults, setProjection, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      birthDate: new Date(new Date().getFullYear() - 30, 0, 1).toISOString().split('T')[0],
      gender: 'male',
      retirementAge: '65', // Будет автоматически обновлено в useEffect
      salaryAmount: '80000',
      workExperienceYears: '5',
      additionalExperienceYears: '0',
      futureWorkYears: '30',
      savingsAmount: '100000',
      monthlyContribution: '5000',
      annualIncreaseRate: '4',
      investmentReturnRate: '7',
      inflationRate: '4',
      pensionType: 'both',
      voluntaryPensionType: 'npf',
      npfReturnRate: '6',
      selfInvestmentType: 'conservative',
      includeMaternityCapital: false,
      includeRegionalSupport: false,
      region: 'moscow',
      includeRuralBonus: false,
      includeProfessionalCoefficient: false,
      professionalCategory: 'default',
    });
    setResults(null);
    setProjection([]);
    setErrors({});
    setSaveScenarioName('');
    setShowSaveDialog(false);
  };

  // Экспорт проекции в CSV
  const exportToCSV = () => {
    if (!projection || projection.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = [
      'Год', 'Возраст', 'Ежемесячный доход', 'Ежемесячный взнос', 
      'Накопления', 'Страховая пенсия', 'Накопительная пенсия',
      'Региональная доплата', 'Сельская надбавка', 'Общая пенсия', 'ИПК'
    ];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = projection.map(item => {
      return [
        item.year,
        item.age,
        formatCsvNumber(item.monthlyIncome),
        formatCsvNumber(item.monthlySavingsContribution),
        formatCsvNumber(item.cumulativeSavings),
        formatCsvNumber(item.insurancePension),
        formatCsvNumber(item.voluntaryPension),
        formatCsvNumber(item.regionalBonus),
        formatCsvNumber(item.ruralBonus),
        formatCsvNumber(item.totalPension),
        formatCsvNumber(item.ipkAccumulated)
      ].join(';');
    });

    // Добавляем основную информацию в начало файла
    const infoRows = [
      ['Дата рождения:', new Date(formData.birthDate).toLocaleDateString('ru-RU')],
      ['Пол:', formData.gender === 'male' ? 'Мужской' : 'Женский'],
      ['Возраст выхода на пенсию:', `${results.retirementAge} лет`],
      ['Тип пенсии:', formData.pensionType === 'mandatory' ? 'Страховая' : (formData.pensionType === 'voluntary' ? 'Накопительная' : 'Смешанная')],
      ['Общий стаж:', `${results.totalExperience} лет`],
      ['Общая пенсия при выходе на пенсию:', `${formatCsvNumber(results.monthlyPension)} ₽`],
      ['Коэффициент замещения:', `${formatCsvNumber(results.replacementRate)}%`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'пенсионный_прогноз.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения сценария
  const handleSaveScenario = () => {
    if (showSaveDialog) {
      const savedResult = saveScenario(saveScenarioName || null);
      if (savedResult) {
        alert(`Сценарий "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveScenarioName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePension();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-emerald-500" />
          Параметры расчета пенсии
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Личные данные */}
        <PersonalDataSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Стаж работы */}
        <WorkExperienceSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Финансовые данные */}
        <FinancialDataSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          investmentStrategies={investmentStrategies}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Профессиональные коэффициенты */}
        {formData.includeProfessionalCoefficient && (
          <ProfessionalCategorySection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            professionalCategories={professionalCategories}
          />
        )}

        {/* Региональные доплаты */}
        {formData.includeRegionalSupport && (
          <RegionalBonusesSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            russianRegions={russianRegions}
          />
        )}

        {/* Диалог сохранения сценария */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveScenarioName}
                onChange={(e) => setSaveScenarioName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveScenario}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать пенсию'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {projection.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать прогноз (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveScenario}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;